import React, { useEffect, useMemo, useState } from "react";
import { Row, Col } from "reactstrap";
import CustomForm from "../../custom/Forms/CustomForm.js";
import { useNavigate, useParams } from "react-router-dom";
import { createPageData, basicFormData } from "./SparePartInventoryFile.js";
import CheckboxInput from "../../custom/components/CheckBox-Input/CheckboxInput.js";
import { validate } from "../../custom/validations/Validate.js";
import { getRecords, saveRecords } from "../../services/api.service.js";
import { toast } from "react-toastify";
import { LoaderCreatePage } from "../Loader/Loader.js";
import { loaderOverlay } from "../../Style/loaderOverly.js";
import NumberInput from "../../custom/components/Number-Input/NumberInput.js";
import SelectInput from "../../custom/components/Select-Input/SelectInput.js";
import TextInput from "../../custom/components/Text-Input/TextInput.js";

const SparePartInventoryCreate = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const {
    payload,
    navigatePage,
    headerName,
    validationType,
    endpoint,
    getEndpoint,
  } = createPageData();

  const schema = localStorage.getItem("schema");
  const initialPayload = useMemo(() => payload, []);
  const [loadingData, setLoadingData] = useState(id ? true : false);
  const [formData, setFormData] = useState(basicFormData());
  const [formErrors, setFormErrors] = useState({});
  // role
  const [selectedItem, setSelectedItem] = useState(null);
  const [itemSelectOptions, setItemSelectOptions] = useState([]);
  // current main-stock quantity of the selected item (read only)
  const [availableQty, setAvailableQty] = useState("");
  // read-only, auto-fetched from the selected GMDS Code's material master
  const [itemDescription, setItemDescription] = useState("");

  useEffect(() => {
    if (id) {
      getEditData(id);
    }
  }, [id]);

  useEffect(() => {
    geMaterialsData();
  }, []);

  const getEditData = async (id) => {
    setLoadingData(true);
    try {
      let payload = {
        modelName: initialPayload?.modelName,
        schema: initialPayload?.schema,
        include: initialPayload?.include,
      };

      if (id) {
        payload.whereCondition = { id: id };
      }
      const responseData = await getRecords(payload, getEndpoint);

      if (responseData?.data && responseData.data.length > 0) {
        const data = responseData.data[0];
        setFormData({ inputData: data });
        setSelectedItem({
          value: data?.material_id,
          label: data?.material?.gmds_code || data?.material?.name,
        });
        setAvailableQty(data?.material?.inventory?.quantity ?? 0);
        setItemDescription(data?.material?.descriptions || "");
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoadingData(false);
    }
  };

  const geMaterialsData = async () => {
    setLoadingData(true);
    try {
      let payload = {
        modelName: "material_masters",
        schema: schema,
        whereCondition: {
          is_active: true,
        },
        include: [
          {
            model: "category_masters",
            as: "category",
            where: {
              type: 1,
            },
            required: true,
          },
        ],
      };

      const responseData = await getRecords(payload, "/getRecords");
      const newArray = responseData.data?.map((item) => ({
        value: item.id,
        label: item.gmds_code || item.name,
        // carried on the option so selecting a GMDS Code can auto-fill Alert Qty
        min_stock_qty: item.min_stock_qty,
        // carried on the option so selecting a GMDS Code can auto-fill Item description
        descriptions: item.descriptions,
      }));
      setItemSelectOptions(newArray);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
    setLoadingData(false);
  };

  // main-stock quantity is derived from the inventories table, never typed in
  const getAvailableQty = async (materialId) => {
    if (!materialId) {
      setAvailableQty("");
      return;
    }
    try {
      const responseData = await getRecords(
        {
          modelName: "inventories",
          schema: schema,
          whereCondition: { material_id: materialId, is_active: true },
        },
        "/getRecords"
      );
      setAvailableQty(responseData?.data?.[0]?.quantity ?? 0);
    } catch (error) {
      console.error("Error fetching available quantity:", error);
      setAvailableQty("");
    }
  };

  const onInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    setFormErrors((prevformErrors) => {
      const fieldError = validate(validationType, { [name]: value });
      return {
        ...prevformErrors,
        [name]: fieldError[name] || null,
      };
    });

    setFormData((prevState) => ({
      ...prevState,
      inputData: {
        ...prevState.inputData,
        [name]:
          type === "checkbox"
            ? checked === true
              ? 1
              : 0
            : name === "alt_mobile_number" && value === ""
            ? null
            : value,
      },
    }));
  };

  const handleItemSelect = async (options) => {
    const selectedValue = options ? options.value : "";
    const fieldError = validate(validationType, {
      material_id: selectedValue,
    });

    setSelectedItem(options);
    getAvailableQty(selectedValue);
    setItemDescription(options ? options.descriptions || "" : "");
    setFormErrors((prevformErrors) => ({
      ...prevformErrors,
      material_id: fieldError?.material_id || "",
    }));

    // Alert Qty defaults to the material master's minimum stock qty
    const minStockQty = options
      ? options.min_stock_qty ?? 0
      : "";

    setFormData((prevState) => ({
      ...prevState,
      inputData: {
        ...prevState.inputData,
        material_id: selectedValue,
        alert_qty: minStockQty === "" ? "" : String(minStockQty),
      },
    }));

    setFormErrors((prevformErrors) => ({
      ...prevformErrors,
      alert_qty: null,
    }));
  };

  useEffect(() => {
    if (formErrors?.material_id) {
      setFormErrors((prev) => ({
        ...prev,
        material_id: "GMDS Code cannot be blank.",
      }));
    }
  }, [formErrors?.material_id]);

  const handleSubmit = async (e) => {
    try {
      e.preventDefault();
      setLoadingData(true);

      const userDetails = JSON.parse(localStorage.getItem("authUser"));
      const validationformErrors = validate(validationType, formData.inputData);
      setFormErrors(validationformErrors);
      if (JSON.stringify(validationformErrors) !== "{}") return;
      // Additional validation for `user_documents`

      let payload = {
        modelName: initialPayload?.modelName,
        schema: initialPayload?.schema,
      };

      if (id) {
        payload = { ...payload, id: id, inputData: formData.inputData };
      } else {
        // create
        formData.inputData.created_by = userDetails?.id || "";
        payload = { ...payload, inputData: formData.inputData };
      }
      payload = {
        ...payload,
        inputData: {
          ...payload.inputData,
          quantity: parseInt(formData.inputData.quantity),
          alert_qty:
            formData.inputData.alert_qty === "" ||
            formData.inputData.alert_qty === null ||
            formData.inputData.alert_qty === undefined
              ? 0
              : parseInt(formData.inputData.alert_qty),
          item_location: formData.inputData.item_location || null,
        },
      };

      // console.log(payload)
      const responseData = await saveRecords(payload, endpoint);

      // Handle response
      if (responseData && responseData.code === 1) {
        toast.success(
          `${headerName} ${id ? "updated" : "saved"} successfully!`
        );
        navigate(navigatePage?.back);
      } else {
        toast.error(
          responseData.message || `Error while saving ${headerName}!`
        );
      }
    } catch (error) {
      console.error("Error during submission:", error);
      toast.error("An unexpected error occurred. Please try again.");
    } finally {
      setLoadingData(false);
    }
  };

  // add document

  return (
    <CustomForm
      title={id ? "UPDATE " + headerName : "CREATE " + headerName}
      loadingData={loadingData}
      onFormSubmit={handleSubmit}
      handleBackClick={() => navigate(navigatePage?.back)}
      id={id}
    >
      <Row className="mt-2">
        <Col md="6" className="mt-3">
          <SelectInput
            label="GMDS Code"
            options={itemSelectOptions}
            value={selectedItem}
            onSelect={handleItemSelect}
            placeholder="Select GMDS Code"
            inputClassName="form-control"
            labelClassName="form-label"
            asterisk={true}
            customError={formErrors?.material_id}
          />
        </Col>

        <Col md="6" className="mt-3">
          <TextInput
            label="Item Description"
            name="item_description"
            value={itemDescription}
            onChange={() => {}}
            placeholder="-"
            inputClassName="form-control"
            labelClassName="form-label"
            readOnly={true}
          />
        </Col>

        <Col md="6" className="mt-3">
          <NumberInput
            label="Add Quantity"
            name="quantity"
            value={formData?.inputData?.quantity}
            onChange={onInputChange}
            placeholder="Enter Quantity"
            inputClassName="form-control"
            labelClassName="form-label"
            asterisk={true}
            customError={formErrors?.quantity}
          />
        </Col>

        <Col md="6" className="mt-3">
          <TextInput
            label="Available Qty"
            name="available_qty"
            value={availableQty === "" ? "" : String(availableQty)}
            onChange={() => {}}
            placeholder="-"
            inputClassName="form-control"
            labelClassName="form-label"
            readOnly={true}
          />
        </Col>

        <Col md="6" className="mt-3">
          <NumberInput
            label="Alert Qty"
            name="alert_qty"
            value={formData?.inputData?.alert_qty ?? ""}
            onChange={onInputChange}
            placeholder="Enter Minimum Stock Qty"
            inputClassName="form-control"
            labelClassName="form-label"
            asterisk={true}
            customError={formErrors?.alert_qty}
          />
        </Col>

        <Col md="6" className="mt-3">
          <TextInput
            label="Item Location"
            name="item_location"
            value={formData?.inputData?.item_location || ""}
            onChange={onInputChange}
            placeholder="Enter Item Location"
            inputClassName="form-control"
            labelClassName="form-label"
            customError={formErrors?.item_location}
          />
        </Col>
      </Row>

      <Row className="mt-4">
        <Col lg="12">
          <CheckboxInput
            name="is_active"
            label="Is Active"
            value={formData?.inputData?.is_active}
            checked={formData?.inputData?.is_active}
            handleTextChange={onInputChange}
            parentClassName="form-check"
            checkBoxClassName="form-check-input"
            labelClassName="form-check-label"
          />
        </Col>
      </Row>
      {loadingData && (
        <div style={loaderOverlay}>
          <LoaderCreatePage />
        </div>
      )}
    </CustomForm>
  );
};

export default SparePartInventoryCreate;
